/**
 * CPV Integration Service
 * Integrates CreatorPerformanceService with campaign creator assignment
 * Calculates and stores per-creator CPV rates when creators are assigned to campaigns
 */

import { supabase } from '../lib/supabase';
import { CreatorPerformanceService } from './creatorPerformanceService';
import { CreatorCPVRateManager } from './creatorCPVRateManager';

export interface CreatorMetrics {
  followers: number;
  engagement_rate: number;
  average_views: number;
  total_posts?: number;
  total_reach?: number;
}

export interface CPVCalculationResult {
  baseCPV: number;
  adjustedCPV: number;
  performanceScore: number;
  performanceTier: string;
  performanceMultiplier: number;
  metricsSnapshot: CreatorMetrics;
}

export class CPVIntegrationService {
  /**
   * Calculate and store CPV for a creator being assigned to a campaign
   * Called when a creator is approved for a per-view campaign
   */
  static async calculateAndStoreCPV(
    campaignId: string,
    creatorId: string,
    baseCPV: number
  ): Promise<CPVCalculationResult> {
    try {
      console.log('📊 CPVIntegrationService: Starting CPV calculation', {
        campaignId,
        creatorId,
        baseCPV
      });

      // 1. Fetch creator's Instagram metrics
      const metrics = await this.getCreatorMetrics(creatorId);
      console.log('📈 Fetched creator metrics:', metrics);

      if (!metrics) {
        console.warn('⚠️ Creator metrics not found, using default CPV');
        return {
          baseCPV,
          adjustedCPV: baseCPV,
          performanceScore: 0.5,
          performanceTier: 'mid',
          performanceMultiplier: 1.0,
          metricsSnapshot: {
            followers: 0,
            engagement_rate: 0,
            average_views: 0
          }
        };
      }

      // 2. Convert to performance calculation format
      const performanceMetrics = {
        followers: metrics.followers,
        totalPosts: metrics.total_posts || 100, // Default estimate
        totalLikes: Math.round(metrics.engagement_rate * metrics.followers),
        totalComments: 0, // Calculate from engagement rate
        totalViews: metrics.average_views * (metrics.total_posts || 100),
        totalReach: metrics.total_reach || metrics.average_views * (metrics.total_posts || 100) * 0.8
      };

      console.log('🧮 Performance metrics for calculation:', performanceMetrics);

      // 3. Calculate performance score and adjusted CPV
      const performanceScore = CreatorPerformanceService.calculatePerformanceScore(performanceMetrics);
      const adjustedCPV = CreatorPerformanceService.calculateAdjustedCPV(baseCPV, performanceMetrics);

      console.log('✅ Calculated CPV:', {
        performanceScore: performanceScore.performanceMultiplier,
        performanceTier: performanceScore.tier,
        baseCPV,
        adjustedCPV
      });

      // 4. Store in database
      await CreatorCPVRateManager.storeCreatorCPVRate(
        campaignId,
        creatorId,
        baseCPV,
        performanceMetrics
      );

      console.log('💾 Stored creator CPV rate in database');

      return {
        baseCPV,
        adjustedCPV,
        performanceScore: performanceScore.totalScore,
        performanceTier: performanceScore.tier,
        performanceMultiplier: performanceScore.performanceMultiplier,
        metricsSnapshot: metrics
      };
    } catch (error) {
      console.error('❌ Error in calculateAndStoreCPV:', error);
      throw error;
    }
  }

  /**
   * Get creator's Instagram metrics from creator_profiles table
   */
  private static async getCreatorMetrics(creatorId: string): Promise<CreatorMetrics | null> {
    try {
      const { data, error } = await supabase
        .from('creator_profiles')
        .select('followers, engagement_rate, average_views, total_posts, total_reach')
        .eq('id', creatorId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching creator metrics:', error);
        return null;
      }

      return data as CreatorMetrics | null;
    } catch (error) {
      console.error('Exception fetching creator metrics:', error);
      return null;
    }
  }

  /**
   * Get campaign's base CPV
   */
  static async getCampaignBaseCPV(campaignId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('cost_per_view')
        .eq('id', campaignId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching campaign CPV:', error);
        return 0;
      }

      return data?.cost_per_view || 0;
    } catch (error) {
      console.error('Exception fetching campaign CPV:', error);
      return 0;
    }
  }

  /**
   * Get all creators' CPV rates for a campaign
   */
  static async getCampaignCPVBreakdown(campaignId: string) {
    try {
      const breakdown = await CreatorCPVRateManager.getCampaignCPVStats(campaignId);
      return breakdown;
    } catch (error) {
      console.error('Error getting CPV breakdown:', error);
      throw error;
    }
  }

  /**
   * Get creator's effective CPV for payment calculation
   */
  static async getCreatorEffectiveCPV(campaignId: string, creatorId: string): Promise<number> {
    try {
      const cpv = await CreatorCPVRateManager.getEffectiveCPV(campaignId, creatorId);
      return cpv;
    } catch (error) {
      console.error('Error getting creator effective CPV:', error);
      return 0;
    }
  }

  /**
   * Calculate earnings for a creator with dynamic CPV
   */
  static async calculateCreatorEarnings(
    campaignId: string,
    creatorId: string,
    actualViews: number
  ): Promise<number> {
    try {
      const earnings = await CreatorCPVRateManager.getProjectedEarnings(
        campaignId,
        creatorId,
        actualViews
      );
      return earnings.totalEarnings;
    } catch (error) {
      console.error('Error calculating creator earnings:', error);
      return 0;
    }
  }

  /**
   * Batch calculate CPV for multiple creators in a campaign
   * Used when bulk approving creators
   */
  static async bulkCalculateAndStoreCPV(
    campaignId: string,
    creatorAssignments: Array<{ creatorId: string; baseCPV?: number }>
  ): Promise<CPVCalculationResult[]> {
    try {
      console.log('📊 Bulk calculating CPV for', creatorAssignments.length, 'creators');

      // Get campaign base CPV if not provided per-creator
      const campaignBaseCPV = await this.getCampaignBaseCPV(campaignId);

      const results: CPVCalculationResult[] = [];

      for (const assignment of creatorAssignments) {
        const baseCPV = assignment.baseCPV || campaignBaseCPV;
        try {
          const result = await this.calculateAndStoreCPV(
            campaignId,
            assignment.creatorId,
            baseCPV
          );
          results.push(result);
        } catch (error) {
          console.warn(`⚠️ Failed to calculate CPV for creator ${assignment.creatorId}:`, error);
          // Continue with other creators
        }
      }

      return results;
    } catch (error) {
      console.error('❌ Error in bulkCalculateAndStoreCPV:', error);
      throw error;
    }
  }

  /**
   * Get performance summary for dashboard display
   */
  static async getPerformanceSummary(
    campaignId: string,
    creatorId: string
  ) {
    try {
      const { data, error } = await supabase
        .from('creator_cpv_rates')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('creator_id', creatorId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching performance summary:', error);
        return null;
      }

      if (!data) {
        return null;
      }

      return {
        baseCPV: data.base_cpv,
        adjustedCPV: data.adjusted_cpv,
        performanceScore: data.performance_score,
        performanceTier: data.performance_tier,
        performanceMultiplier: (data.adjusted_cpv / data.base_cpv).toFixed(2),
        metricsSnapshot: data.metrics_snapshot,
        appliedAt: data.applied_at
      };
    } catch (error) {
      console.error('Exception getting performance summary:', error);
      return null;
    }
  }
}
